/**
 * Maps and boss spawning - the decided constants only.
 *
 * HP/damage formulas per map, Combat Power thresholds and mob rosters are
 * pending balance work (`docs/OPEN_QUESTIONS.md`). What is decided:
 *
 * - 20 playable maps at launch, 30 locked for future updates.
 * - Base mob scale grows 10% per map (compounding).
 * - Boss spawns at a random time between 3 and 16 minutes.
 * - Boss visual scale is 2x..13x the map's mob scale, clamped by the viewport.
 * - On player death every enemy is removed and the boss timer restarts.
 */

import {
  assertSafeInteger,
  assertSafeIntegerInRange,
  LOCKED_MAP_COUNT,
  PLAYABLE_MAP_COUNT,
  TOTAL_MAP_COUNT,
} from '@kenocyber/shared';
import type { SimulationRng } from './simulation-rng';

// Single source of truth for map counts lives in the shared catalog.
export { LOCKED_MAP_COUNT, PLAYABLE_MAP_COUNT, TOTAL_MAP_COUNT };

/** +10% per map, compounding, expressed as basis points growth per step. */
export const MOB_SCALE_GROWTH_BPS_PER_MAP = 1000;

/**
 * Mob scale multiplier for a 1-based map index, relative to map 1.
 *
 * Computed in integer basis points with bigint so map 50 (1.1^49) is exact and
 * identical on every runtime; the caller converts to its own unit at the edge.
 * Returns basis points: map 1 -> 10000, map 2 -> 11000, map 3 -> 12100, ...
 */
export function mobScaleBpsForMap(mapIndex: number): bigint {
  assertSafeIntegerInRange(mapIndex, 1, TOTAL_MAP_COUNT, 'mapIndex');
  let scale = 10_000n;
  for (let step = 1; step < mapIndex; step += 1) {
    scale = (scale * BigInt(10_000 + MOB_SCALE_GROWTH_BPS_PER_MAP)) / 10_000n;
  }
  return scale;
}

/**
 * **+35% por mapa, composto: a vida do mob E a recompensa dele** (V28I.BP).
 *
 * ## Um literal, dois consumidores
 *
 * Este é o único sítio onde o 3500 existe. `MOB_HEALTH_GROWTH_PER_MAP` deriva
 * dele — `(10000 + 3500) / 10000` é exactamente o `1.35` que estava escrito
 * lá, porque a IEEE-754 arredonda correctamente o quociente de dois inteiros
 * representáveis — e `mapRewardBpsForMap` deriva dele também.
 *
 * É essa partilha que torna "a recompensa acompanha a vida 1:1" uma propriedade
 * da ESTRUTURA e não uma coincidência de dois números iguais escritos em
 * ficheiros diferentes. Mudar a dificuldade sem mudar o ganho passa a ser
 * impossível por construção, que é exactamente o defeito que o gate V28I.BO
 * mediu: 299,5x mais vida no mapa 20 pela mesma tarifa do mapa 1.
 *
 * Deliberadamente SEPARADO de {@link MOB_SCALE_GROWTH_BPS_PER_MAP}: aquele é o
 * TAMANHO desenhado (+10%), e tamanho não é dificuldade nem recompensa.
 */
export const MAP_DIFFICULTY_GROWTH_BPS_PER_MAP = 3500;

/**
 * O multiplicador de RECOMPENSA-BASE de um mapa, em basis points.
 *
 * Mapa 1 -> 10000, mapa 2 -> 13500, mapa 3 -> 18225, ..., mapa 20 -> 2994364.
 *
 * A MESMA aritmética de {@link mobScaleBpsForMap}, e de propósito: inteiros em
 * `bigint`, um passo de cada vez, multiplicar e dividir por 10000 em cada passo.
 * Nenhum ponto flutuante entra numa conta que acaba no livro-razão, e o mapa 50
 * é exacto em qualquer motor.
 *
 * O que ele multiplica é a recompensa-BASE por inimigo, antes de qualquer
 * passiva — ver `hunt/simulation.ts`. Ele nunca multiplica o plano por inimigo
 * que um artefacto concede: "+1 Ouro por morte" é o que o artefacto diz, e não
 * "+1 Ouro por morte vezes o mapa".
 */
export function mapRewardBpsForMap(mapIndex: number): bigint {
  assertSafeIntegerInRange(mapIndex, 1, TOTAL_MAP_COUNT, 'mapIndex');
  let reward = 10_000n;
  for (let step = 1; step < mapIndex; step += 1) {
    reward = (reward * BigInt(10_000 + MAP_DIFFICULTY_GROWTH_BPS_PER_MAP)) / 10_000n;
  }
  return reward;
}

/**
 * **A curva é aceite em todo o catálogo e cresce estritamente.**
 *
 * Corre ao carregar, e não numa suíte: um passo mal editado — zero, negativo,
 * ou um erro de sinal — tem de falhar no arranque, e não na primeira morte de
 * um jogador. É a mesma disciplina das provas do catálogo de peixes.
 */
function assertMapRewardCurveIsSound(): void {
  if (mapRewardBpsForMap(1) !== 10_000n) {
    throw new RangeError('map 1 must be the reward baseline of 10000 bps');
  }
  let previous = 0n;
  for (let mapIndex = 1; mapIndex <= TOTAL_MAP_COUNT; mapIndex += 1) {
    const reward = mapRewardBpsForMap(mapIndex);
    if (reward <= previous) {
      throw new RangeError(
        `map reward must grow strictly; map ${mapIndex} is ${reward} after ${previous}`,
      );
    }
    previous = reward;
  }
}

assertMapRewardCurveIsSound();

export const BOSS_SPAWN_MIN_SECONDS = 3 * 60;
export const BOSS_SPAWN_MAX_SECONDS = 16 * 60;

export const BOSS_SCALE_MIN_MULTIPLIER = 2;
export const BOSS_SCALE_MAX_MULTIPLIER = 13;

/**
 * Draws the next boss spawn delay from the authoritative RNG.
 *
 * The SERVER draws this and tells the client; the client never decides when a
 * boss appears. Uniform distribution is the initial choice and is configurable.
 *
 * Simulation-grade randomness is correct here: a boss timer carries no direct
 * economic value, and the player seeing it coming is harmless.
 */
export function rollBossSpawnDelaySeconds(rng: SimulationRng): number {
  return rng.nextIntInclusive(BOSS_SPAWN_MIN_SECONDS, BOSS_SPAWN_MAX_SECONDS);
}

/**
 * Everything that restarts the countdown to the next boss.
 *
 * All three are decided: the boss dying, the boss escaping past the bottom
 * boundary, and the player dying.
 */
export const BOSS_TIMER_RESET_TRIGGERS = ['boss_defeated', 'boss_escaped', 'player_death'] as const;

export type BossTimerResetTrigger = (typeof BOSS_TIMER_RESET_TRIGGERS)[number];

export function resetsBossTimer(trigger: BossTimerResetTrigger): boolean {
  return (BOSS_TIMER_RESET_TRIGGERS as readonly string[]).includes(trigger);
}

/**
 * Draws the boss scale multiplier and clamps it so the sprite fits the visible
 * play area. `maxFittingMultiplier` comes from the renderer (viewport height /
 * mob sprite height); the roll itself stays deterministic and auditable.
 */
export function rollBossScaleMultiplier(rng: SimulationRng, maxFittingMultiplier: number): number {
  if (!Number.isFinite(maxFittingMultiplier)) {
    throw new RangeError(`maxFittingMultiplier must be finite, received ${maxFittingMultiplier}`);
  }
  const rolled = rng.nextIntInclusive(BOSS_SCALE_MIN_MULTIPLIER, BOSS_SCALE_MAX_MULTIPLIER);
  const fitting = assertSafeInteger(
    Math.floor(maxFittingMultiplier),
    'maxFittingMultiplier (floored)',
  );
  return Math.min(rolled, Math.max(BOSS_SCALE_MIN_MULTIPLIER, fitting));
}

/** To advance: minimum Combat Power (pending formula) AND the boss must die. */
export interface MapAdvanceRequirement {
  /** PENDING - Combat Power formula not designed yet. */
  readonly minimumCombatPower: null;
  readonly mustDefeatBoss: true;
}
